import * as React from "react";
import { FileText, Settings2, Upload, X, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { OrgKnowledgeBaseRow, OrgTemplateRow } from "../../../features/projects/create-project-wizard/wizardTypes";
import type { SelectedTemplateWithConfig } from "../../../features/projects/create-project-wizard/wizardTypes";

interface WizardResourcesFormProps {
  projectName: string;
  onProjectNameChange: (v: string) => void;
  showProjectName: boolean;
  resourcesHint?: string | null;
  orgTemplates: OrgTemplateRow[];
  orgKbs: OrgKnowledgeBaseRow[];
  selectedTemplates: SelectedTemplateWithConfig[];
  onToggleTemplate: (t: OrgTemplateRow, add: boolean) => void;
  selectedKbIds: string[];
  onToggleKb: (id: string, add: boolean) => void;
  uploadedFiles: File[];
  onUploadedFilesChange: (files: File[]) => void;
  onOpenTemplateConfig: (cfg: SelectedTemplateWithConfig) => void;
}

export const WizardResourcesForm: React.FC<WizardResourcesFormProps> = ({
  projectName,
  onProjectNameChange,
  showProjectName,
  resourcesHint,
  orgTemplates,
  orgKbs,
  selectedTemplates,
  onToggleTemplate,
  selectedKbIds,
  onToggleKb,
  uploadedFiles,
  onUploadedFilesChange,
  onOpenTemplateConfig,
}) => {
  const uploadRef = React.useRef<HTMLInputElement>(null);
  const selectedById = React.useMemo(() => new Map(selectedTemplates.map((c) => [c.id, c])), [selectedTemplates]);

  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    const next: File[] = [...uploadedFiles];
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      const lower = f.name.toLowerCase();
      if (lower.endsWith(".docx") || lower.endsWith(".xlsx")) {
        next.push(f);
      }
    }
    onUploadedFilesChange(next);
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      {showProjectName && (
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Project name *</Label>
          <Input
            className="h-9 text-sm"
            value={projectName}
            onChange={(e) => onProjectNameChange(e.target.value)}
            placeholder="e.g. Q1 Report"
          />
        </div>
      )}

      {resourcesHint ? <p className="text-xs text-muted-foreground leading-snug">{resourcesHint}</p> : null}

      <div className="space-y-2">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Catalog templates
        </Label>
        <ScrollArea className="h-[140px] border rounded-md bg-white">
          <div className="p-2 space-y-1">
            {orgTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground px-1 py-2">No templates in organization.</p>
            ) : (
              orgTemplates.map((t) => {
                const on = selectedById.has(t.id);
                return (
                  <div
                    key={t.id}
                    className="flex items-center justify-between gap-2 rounded border border-transparent px-2 py-1.5 hover:bg-muted/50"
                  >
                    <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => onToggleTemplate(t, e.target.checked)}
                      />
                      <span className="truncate font-medium">{t.name}</span>
                    </label>
                    {on && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 shrink-0"
                        onClick={() => onOpenTemplateConfig(selectedById.get(t.id)!)}
                        title="Variables & blocks"
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <BookOpen className="h-3 w-3" />
          Knowledge bases
        </Label>
        <ScrollArea className="h-[100px] border rounded-md bg-white">
          <div className="p-2 space-y-1">
            {orgKbs.length === 0 ? (
              <p className="text-xs text-muted-foreground px-1 py-2">No shared KBs.</p>
            ) : (
              orgKbs.map((kb) => (
                <label key={kb.id} className="flex items-center gap-2 text-xs px-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedKbIds.includes(kb.id)}
                    onChange={(e) => onToggleKb(kb.id, e.target.checked)}
                  />
                  <span className="truncate">{kb.name}</span>
                </label>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <Upload className="h-3 w-3" />
          Upload .docx / .xlsx
        </Label>
        <input ref={uploadRef} type="file" accept=".docx,.xlsx" multiple className="hidden" onChange={handleUploadChange} />
        <Button type="button" variant="outline" size="sm" className="text-xs h-8 w-full" onClick={() => uploadRef.current?.click()}>
          Add files
        </Button>
        {uploadedFiles.length > 0 && (
          <ul className="space-y-1">
            {uploadedFiles.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1">
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground p-0.5"
                  onClick={() => onUploadedFilesChange(uploadedFiles.filter((_, j) => j !== i))}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
